export type ImageSource = { fileId: string | null; url: string | null };
export type ImageSourcePatch = { fileId?: string | null; url?: string | null };

const imageExtension = /\.(svg|png|jpe?g|webp|gif|avif|ico)$/i;
const localHostSuffixes = ["localhost", "localdomain", "local", "lan", "internal", "home", "home.arpa"];

export function isValidImageUrl(value: string): boolean {
  if (typeof value !== "string" || !/^https:\/\//i.test(value) || /\s|\\/.test(value)
    || [...value].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return false;

  // Reject syntax the URL parser silently normalizes, including encoded hosts and empty credentials/fragments.
  const authority = value.slice("https://".length).split(/[/?#]/, 1)[0];
  if (!authority || /[@%]/.test(authority) || value.includes("#")) return false;

  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.username || url.password || url.hash || !imageExtension.test(url.pathname)) return false;

    const host = url.hostname;
    const labels = host.split(".");
    if (host.length > 253 || labels.length < 2
      || labels.some((label) => !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label))) return false;

    // URL canonicalizes decimal/octal/hex IPv4; a DNS suffix also excludes all IP literals.
    if (!/^(?:[a-z]{2,63}|xn--[a-z0-9-]+)$/.test(labels[labels.length - 1])) return false;
    return !localHostSuffixes.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
  } catch {
    return false;
  }
}

export function resolveImageSource(fileId?: string | null, url?: string | null): string | null {
  if (typeof url === "string" && isValidImageUrl(url)) return url;
  const id = normalizeFileId(fileId);
  return id ? `/api/brand-assets/${encodeURIComponent(id)}` : null;
}

export function resolveAdminLogoSource(settings: {
  adminLogoFileId?: string | null;
  adminLogoUrl?: string | null;
  adminLogoDarkFileId?: string | null;
  adminLogoDarkUrl?: string | null;
}, theme: "light" | "dark"): string | null {
  const light = resolveImageSource(settings.adminLogoFileId, settings.adminLogoUrl);
  return theme === "dark"
    ? resolveImageSource(settings.adminLogoDarkFileId, settings.adminLogoDarkUrl) ?? light
    : light;
}

export function mergeImageSource(existing: ImageSource, patch: ImageSourcePatch): ImageSource {
  const newFileId = normalizeFileId(patch.fileId);
  if (patch.url != null && !isValidImageUrl(patch.url)) throw new Error("Invalid image URL");
  if (newFileId && patch.url) throw new Error("Choose one image source");
  if (patch.url) return { fileId: null, url: patch.url };
  if (newFileId) return { fileId: newFileId, url: null };

  const url = patch.url === null ? null : (typeof existing.url === "string" && isValidImageUrl(existing.url) ? existing.url : null);
  return {
    fileId: url ? null : normalizeFileId(patch.fileId === undefined ? existing.fileId : patch.fileId),
    url,
  };
}

function normalizeFileId(value: string | null | undefined): string | null {
  return typeof value === "string" ? value.trim() || null : null;
}
